// src/pages/Credits.js
// Buy credits (Razorpay) + balance + recent transactions.
// Sibling page is Subscribe.js — the header toggle switches between them.

import { useEffect, useState } from "react";
import { credits as creditsApi } from "../api";
import { openCheckout } from "../lib/razorpay";

export default function Credits({ user, onUser, onNavigate }) {
  const [packages, setPackages] = useState([]);
  const [keyId, setKeyId] = useState("");
  const [balance, setBalance] = useState(null);
  const [txns, setTxns] = useState([]);
  const [buying, setBuying] = useState(null); // package_id in-flight
  const [msg, setMsg] = useState("");
  const [err, setErr] = useState("");

  const refresh = async () => {
    try {
      const [pkgs, bal, tx] = await Promise.all([
        creditsApi.packages(),
        creditsApi.balance(),
        creditsApi.transactions(10).catch(() => ({ transactions: [] })),
      ]);
      setPackages(pkgs.packages || []);
      setKeyId(pkgs.razorpay_key_id || "");
      setBalance(bal);
      setTxns(tx.transactions || []);
    } catch (e) {
      setErr(e.message);
    }
  };

  useEffect(() => {
    refresh();
  }, []);

  const buy = async (pkg) => {
    setErr("");
    setMsg("");
    setBuying(pkg.id);
    try {
      const order = await creditsApi.createOrder(pkg.id);
      const resp = await openCheckout({
        key: order.key_id || keyId,
        amount: (order.amount || pkg.price) * 100, // create-order returns INR → paise
        currency: order.currency || "INR",
        orderId: order.order_id,
        name: "Karvi AI",
        description: pkg.label || `${pkg.credits} credits`,
        prefill: { email: user?.email, name: user?.username },
      });
      const verified = await creditsApi.verify({
        razorpay_order_id: resp.razorpay_order_id,
        razorpay_payment_id: resp.razorpay_payment_id,
        razorpay_signature: resp.razorpay_signature,
      });
      setMsg(`✓ ${verified.credits_added} credits added.`);
      onUser?.((u) => (u ? { ...u, credits: verified.new_balance } : u));
      refresh();
    } catch (e) {
      if (e.message !== "Payment cancelled") setErr(e.message);
    } finally {
      setBuying(null);
    }
  };

  return (
    <div className="page">
      <div className="billing__tabs">
        <button className="billing__tab billing__tab--on">Buy credits</button>
        <button className="billing__tab" onClick={() => onNavigate("subscribe")}>Plans</button>
      </div>

      <div className="balance">
        <div>
          <div className="balance__label">Your balance</div>
          <div className="balance__value">◆ {Math.floor(balance?.credits ?? user?.credits ?? 0)}</div>
        </div>
        <div className="balance__meta">
          <span>Purchased {balance?.total_credits_purchased ?? 0}</span>
          <span>Used {balance?.total_credits_used ?? 0}</span>
        </div>
      </div>

      {msg && <div className="form-note">{msg}</div>}
      {err && <div className="form-error">{err}</div>}

      <div className="packs">
        {packages.map((p) => (
          <div key={p.id} className={`pack ${p.popular ? "pack--pop" : ""}`}>
            {p.popular && <span className="pack__tag">Most popular</span>}
            <div className="pack__credits">◆ {p.credits}</div>
            <div className="pack__label">credits</div>
            <div className="pack__price">₹{p.price}</div>
            <button className="btn btn--primary btn--block" onClick={() => buy(p)} disabled={buying === p.id}>
              {buying === p.id ? "Opening…" : "Buy"}
            </button>
          </div>
        ))}
      </div>

      {txns.length > 0 && (
        <div className="history">
          <h3 className="history__title">Recent activity</h3>
          {txns.map((t) => (
            <div key={t._id} className="history__row">
              <span className="history__reason">{(t.reason || t.type || "").replace(/_/g, " ")}</span>
              <span className={`history__amt ${t.amount >= 0 ? "pos" : "neg"}`}>
                {t.amount >= 0 ? "+" : ""}{t.amount}
              </span>
            </div>
          ))}
        </div>
      )}

      <p className="fineprint">Credits never expire. Payments are processed securely by Razorpay.</p>
    </div>
  );
}
